import React, { useMemo, useEffect } from 'react';
import { FiPaperclip, FiX } from 'react-icons/fi';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import { Input, Textarea, NumberInput, Select } from '../../components/ui/Input';
import VendorPicker from '../../components/shared/VendorPicker';
import FileUploader from '../../components/ui/FileUploader';
import { useQuery } from '@tanstack/react-query';
import { getSimpleProjectList } from '../../api/projects';

function toNumber(v) {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export default function ProcurementFormInner({ form, setForm, errors, file, setFile, isEdit = false }) {
  const { data: projects = [] } = useQuery({
    queryKey: ['projects', 'simple'],
    queryFn: getSimpleProjectList,
  });

  // Live recompute amount = qty * unitPrice (only if user hasn't overridden)
  const computedAmount = useMemo(
    () => +(toNumber(form.quantity) * toNumber(form.unitPrice)).toFixed(2),
    [form.quantity, form.unitPrice],
  );

  // Auto-sync amount when qty/unitPrice changes
  useEffect(() => {
    setForm((prev) => ({ ...prev, amount: computedAmount }));
  }, [computedAmount, setForm]);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormField
        label="Title"
        required
        error={errors.title}
        className="md:col-span-2"
      >
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="e.g. 5x Dell laptops for engineering"
          error={errors.title}
        />
      </FormField>

      <FormField
        label="Description"
        hint="Provide context, specs, or justification."
        className="md:col-span-2"
      >
        <Textarea
          rows={3}
          value={form.description || ''}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Describe what's being procured and why..."
        />
      </FormField>

      <FormField label="Vendor" hint="Pick from active vendors.">
        <VendorPicker
          value={form.vendorId}
          onChange={(id) => setForm({ ...form, vendorId: id })}
        />
      </FormField>

      <FormField label="Project" required error={errors.projectId}>
        <Select
          value={form.projectId || ''}
          onChange={(e) => setForm({ ...form, projectId: e.target.value ? Number(e.target.value) : '' })}
          error={!!errors.projectId}
        >
          <option value="">Select project…</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.projectName}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField label="Quantity" required error={errors.quantity}>
        <NumberInput
          min={1}
          step="1"
          value={form.quantity}
          onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          error={errors.quantity}
        />
      </FormField>

      <FormField label="Unit Price" required error={errors.unitPrice}>
        <NumberInput
          min={0}
          step="0.01"
          value={form.unitPrice}
          onChange={(e) => setForm({ ...form, unitPrice: e.target.value })}
          error={errors.unitPrice}
        />
      </FormField>

      <FormField
        label="Total Amount"
        required
        error={errors.amount}
        hint="Auto-calculated as quantity × unit price. You can override."
        className="md:col-span-2"
      >
        <NumberInput
          min={0}
          step="0.01"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          error={errors.amount}
        />
      </FormField>

      {!isEdit && (
        <div className="md:col-span-2">
          <div className="mb-1 text-xs font-medium text-ink">Attachment</div>
          {file ? (
            <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="flex min-w-0 items-center gap-2 text-sm">
                <FiPaperclip className="shrink-0 text-ink-muted" />
                <span className="truncate font-medium text-ink">{file.name}</span>
                <span className="shrink-0 text-xs text-ink-muted">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                icon={FiX}
                onClick={() => setFile(null)}
              >
                Remove
              </Button>
            </div>
          ) : (
            <FileUploader
              variant="dropzone"
              onSelect={(f) => setFile(f)}
              buttonLabel="Attach quote / PO"
            />
          )}
          <p className="mt-1 text-xs text-ink-muted">
            Optional — attach a vendor quote, spec sheet, or supporting document.
          </p>
        </div>
      )}
    </div>
  );
}
